import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type Person } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, StatTile, type Column } from '@/components/ui';
import { IconUsers } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox, AccountStatusBadge } from '@/components/admin/shared';
import { Drawer, Field } from '@/components/admin/Drawer';
import { Pager } from '@/components/admin/Pager';
import { personName, dateTime, km } from '@/lib/adminFormat';

const PAGE = 25;

/** Customer Identity — the identity-record view for riders, projected from the
 *  `auth_users` table (scope=customer). Covers the legacy Filament
 *  "Customer Identity Edit" resource (CustomerResource — route /customers, table
 *  auth_users): search a rider, then open a record card that mirrors the
 *  resource's three sections — Identity (name / email / phone / country code /
 *  profile), Wallet (km-credit balance + account status), and Referral
 *  (read-only referral fields). Note: the legacy Identity section also edits
 *  `gender`, but that column isn't in the admin SELECT projection (auth-svc
 *  doesn't surface it), so it's intentionally absent here rather than shown blank.
 *
 *  READ-ONLY (built-readonly): the identity row is OWNED by auth-svc. admin-svc
 *  exposes the SELECT projection (GET /v1/admin/customers, /:id) this page binds
 *  to, but there is no admin identity-WRITE endpoint to proxy yet — auth-svc's
 *  internal sync is not surfaced through admin-svc, and adding it would require
 *  a new cross-service client + RBAC perm + route. So rather than ship dead
 *  "Edit" / "Save" buttons, this is an honest record view: every field the
 *  Filament form edits is shown here read-only, and the two write affordances
 *  the resource carries that ARE wired elsewhere are linked out — wallet
 *  adjustment lives on the Wallet page (finance · wallet.adjust) and account
 *  block/reinstate lives on the Customers drawer (ops · customers.block). */
export function CustomerIdentity() {
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'customer-identity', q, offset],
    queryFn: ({ signal }) => adminApi.customers({ q, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const columns: Array<Column<Person>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-text-muted">#{r.id}</span> },
    { key: 'name', header: 'Name', cell: (r) => <span className="font-medium text-slate-100">{personName(r)}</span> },
    {
      key: 'phone',
      header: 'Phone',
      cell: (r) => (
        <span className="text-slate-300">
          {r.country_code}
          {r.phone}
        </span>
      ),
    },
    { key: 'email', header: 'Email', cell: (r) => <span className="text-slate-300">{r.email || '—'}</span> },
    { key: 'status', header: 'Status', cell: (r) => <AccountStatusBadge status={r.status} /> },
    { key: 'joined', header: 'Joined', cell: (r) => <span className="text-xs text-text-muted">{dateTime(r.created_at)}</span> },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r.id)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          View record
        </button>
      ),
    },
  ];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Customer Identity"
        subtitle="Rider identity records from auth_users — search by name, phone, email, or id."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search riders…"
          />
        }
      />

      <Card flush>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner label="Loading identity records…" />
          </div>
        ) : isError ? (
          <EmptyState title="Couldn't load identity records" description={(error as Error)?.message} />
        ) : (
          <>
            <DataTable<Person>
              columns={columns}
              rows={data?.items ?? []}
              rowKey={(r) => String(r.id)}
              stickyHeader
              empty={
                <EmptyState
                  title="No customers"
                  description="No auth_users rows for scope=customer match this search."
                  icon={<IconUsers className="h-6 w-6" />}
                />
              }
            />
            <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
          </>
        )}
      </Card>

      {selected !== null ? <IdentityDrawer id={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

/** IdentityDrawer — the full identity record for one rider. Mirrors the Filament
 *  CustomerResource edit form's section layout (Identity / Wallet / Referral),
 *  read-only. The wallet + status writes the resource carried live on their own
 *  permission-gated pages; this view links to them rather than duplicating
 *  (potentially ungated) controls. */
function IdentityDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'customer', id],
    queryFn: ({ signal }) => adminApi.customer(id, signal),
  });

  const c = data?.customer;

  return (
    <Drawer
      title={c ? personName(c) : `Customer #${id}`}
      subtitle={c ? `Identity record · auth_users #${c.id}` : undefined}
      onClose={onClose}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner label="Loading record…" />
        </div>
      ) : isError ? (
        <EmptyState title="Couldn't load record" description={(error as Error)?.message} />
      ) : c ? (
        <div className="space-y-5">
          {/* Top KPIs — the at-a-glance state (km-credit float + account status). */}
          <div className="grid grid-cols-2 gap-3">
            <StatTile label="km-credit" value={km(data?.wallet?.balance_km ?? 0)} tone="info" />
            <StatTile
              label="Account status"
              value={<AccountStatusBadge status={c.status} />}
              tone={c.status === 'blocked' ? 'danger' : c.status === 'active' ? 'ok' : 'neutral'}
            />
          </div>

          {/* Identity — the resource's first section. */}
          <Section title="Identity">
            <Field label="ID" value={`#${c.id}`} />
            <Field label="First name" value={c.first_name || '—'} />
            <Field label="Last name" value={c.last_name || '—'} />
            <Field label="Email" value={c.email || '—'} />
            <Field label="Phone" value={c.phone || '—'} />
            <Field label="Country code" value={c.country_code || '—'} />
            <Field
              label="Profile picture"
              value={c.profile_picture ? <span className="metric text-xs">{c.profile_picture}</span> : '—'}
            />
            <Field label="Joined" value={dateTime(c.created_at)} />
          </Section>

          {/* Wallet — the resource's second section. The balance is read-only here;
              manual adjustment is the finance team's wallet.adjust action on the
              Wallet page (audited via the km-credit ledger). */}
          <Section title="Wallet & billing">
            <Field label="km-credit balance" value={km(data?.wallet?.balance_km ?? 0)} />
            <Field
              label="Wallet exists"
              value={data?.wallet?.exists ? 'Yes' : 'No (no top-up / adjustment yet)'}
            />
            <Field label="Last wallet change" value={dateTime(data?.wallet?.updated_at)} />
            <Field label="Stripe customer" value={c.stripe_id ? <span className="metric text-xs">{c.stripe_id}</span> : '—'} />
            <p className="pt-2 text-[12px] text-text-secondary">
              Manual km-credit adjustments are made on the{' '}
              <span className="font-medium text-slate-200">Wallet</span> page (finance role · audited via the
              km-credit ledger), so balance changes stay reconcilable against top-ups.
            </p>
          </Section>

          {/* Referral — the resource's third (collapsed, disabled) section. The
              referral fields aren't in the admin SELECT projection (anti-fraud:
              server-controlled only), so this notes where they're owned. */}
          <Section title="Referral">
            <p className="text-[12px] text-text-secondary">
              Referral code and referrer are server-controlled (anti-fraud) and aren't editable from the admin
              console — matching the legacy form, where both were shown disabled.
            </p>
          </Section>

          {/* Account actions — the block/reinstate write the resource neighboured
              lives on the Customers drawer (ops role · customers.block). Pointed
              to rather than duplicated so it stays single-gated. */}
          <Section title="Account actions">
            <p className="text-[12px] text-text-secondary">
              To block or reinstate this account, open it on the{' '}
              <span className="font-medium text-slate-200">Customers</span> page — the trust &amp; safety control
              there (ops role) flips the status and revokes the rider's active sessions.
            </p>
          </Section>
        </div>
      ) : (
        <EmptyState title="Customer not found" />
      )}
    </Drawer>
  );
}

/** A titled group of Field rows inside the identity drawer — the visual analogue
 *  of a Filament form Section. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">{title}</div>
      <div>{children}</div>
    </Card>
  );
}
